import { useState } from 'react';
import { Button, Card, Icon, Sheet, Switch } from '@astral/ui';
import { DEFAULT_STICKER_EMOJI, type ProjectEmojiPreset } from '@astral/shared';
import { useT } from '../providers/I18nProvider.js';
import { useEmojiPresets } from '../hooks/useEmojiPresets.js';
import { EmojiPresetManager } from './EmojiPresetManager.js';

export interface ProjectPresetControlProps {
  preset: ProjectEmojiPreset | null;
  onChange: (preset: ProjectEmojiPreset | null) => void;
  /** Apply the given sequence cyclically across every sticker in the pack now. */
  onApplyToAll: (emojis: string[]) => void;
  assetsCount: number;
}

/**
 * Project-level default emoji preset: pick "single default" or a saved preset,
 * toggle whether new stickers auto-receive it, and apply it to the whole pack.
 * Editing this NEVER rewrites existing stickers unless "apply to all" is pressed.
 */
export function ProjectPresetControl({
  preset,
  onChange,
  onApplyToAll,
  assetsCount,
}: ProjectPresetControlProps): JSX.Element {
  const t = useT();
  const [chooserOpen, setChooserOpen] = useState(false);
  const [managerOpen, setManagerOpen] = useState(false);
  const { presets } = useEmojiPresets(chooserOpen);

  const hasSequence = Boolean(preset && preset.emojis.length > 0);
  const applyEmojis = hasSequence ? (preset as ProjectEmojiPreset).emojis : [DEFAULT_STICKER_EMOJI];

  return (
    <Card padding="md" className="stack" >
      <div className="row-between">
        <span className="as-field__label" style={{ margin: 0 }}>
          {t('presets.defaultLabel')}
        </span>
        <button className="home__link" onClick={() => setChooserOpen(true)}>
          {t('common.edit')}
        </button>
      </div>

      <div className="preset-current">
        {hasSequence ? (
          <>
            {preset?.name && <span className="preset-current__name">{preset.name}</span>}
            <span className="preset-current__emojis">{applyEmojis.join(' ')}</span>
          </>
        ) : (
          <span className="preset-current__emojis">
            {t('presets.singleDefault')} {DEFAULT_STICKER_EMOJI}
          </span>
        )}
      </div>

      {hasSequence && (
        <div className="row-between">
          <span className="muted" style={{ fontSize: 13 }}>
            {t('presets.autoApply')}
          </span>
          <Switch
            checked={preset?.autoApply ?? false}
            aria-label={t('presets.autoApply')}
            onChange={(checked) =>
              preset && onChange({ ...preset, autoApply: checked })
            }
          />
        </div>
      )}

      <Button
        variant="secondary"
        size="sm"
        disabled={assetsCount === 0}
        leftIcon={<Icon name="sparkles" size={16} />}
        onClick={() => onApplyToAll(applyEmojis)}
      >
        {t('presets.applyToAll')}
      </Button>

      <Sheet open={chooserOpen} onClose={() => setChooserOpen(false)} title={t('presets.defaultLabel')}>
        <div className="stack" style={{ gap: 10 }}>
          <button
            className={`preset-option${!hasSequence ? ' preset-option--active' : ''}`}
            onClick={() => {
              onChange(null);
              setChooserOpen(false);
            }}
          >
            <span className="preset-option__name">{t('presets.singleDefault')}</span>
            <span className="preset-option__emojis">{DEFAULT_STICKER_EMOJI}</span>
          </button>

          {presets.map((p) => (
            <button
              key={p.id}
              className={`preset-option${preset?.name === p.name ? ' preset-option--active' : ''}`}
              onClick={() => {
                onChange({ name: p.name, emojis: p.emojis, autoApply: true });
                setChooserOpen(false);
              }}
            >
              <span className="preset-option__name">{p.name}</span>
              <span className="preset-option__emojis">{p.emojis.join(' ')}</span>
            </button>
          ))}

          <Button
            variant="ghost"
            leftIcon={<Icon name="edit" size={16} />}
            onClick={() => {
              setChooserOpen(false);
              setManagerOpen(true);
            }}
          >
            {t('presets.manage')}
          </Button>
        </div>
      </Sheet>

      <EmojiPresetManager open={managerOpen} onClose={() => setManagerOpen(false)} />
    </Card>
  );
}
